import { checkPermissions } from "@elit-hair/elit-tools";
import { GridColDef, GridEventListener } from "@mui/x-data-grid";
import { GridCellSelectionModel } from "@mui/x-data-grid-premium";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { motion } from "motion/react";
import { MutableRefObject, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import CommonGridTable from "@/components/CommonGridTable";
import { CompanionsColumn } from "@/components/table/CompanionsColumn";
import EstimatedPeopleCountColumn from "@/components/table/EstimatedCompanionsColumn";
import FlightTimeColumn from "@/components/table/FlightTimeColumn";
import HotelNameColumn from "@/components/table/HotelNameColumn";
import PassengerStatusColumn from "@/components/table/PassengerStatusColumn";
import PassengerTransferTypeColumn from "@/components/table/PassengerTransferTypeColumn";
import { PERMISSIONS } from "@/constants/permissions";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import useDriversContext from "@/providers/data/DriversContext";
import useAuth from "@/providers/hooks/useAuth";
import { usePlanActions, usePlanIsFullScreen, usePlanRowModesModel } from "@/stores/use-plan-table";
import { IPlanPassenger } from "@/types/tasks.types";
import { onGridCellKeyDownCallback } from "@/utils/grid";

import { PassengerColumn } from "../../../../components/table/PassengerColumn";
import PassengerTypeColumn from "./PassengerTypeColumn";
import PassengerVisibilityColumn from "./PassengerVisibilityColumn";
import PatientTypeColumn from "./PatientTypeColumn";
import { PlanActionsColumn } from "./PlanActionsColumn";
import { PlanAirportColumn } from "./PlanAirportColumn";
import { PlanAssignationColumn } from "./PlanAssignationColumn";
import { PlanLocationColumn } from "./PlanLocationColumn";
import { PlanNotesColumn } from "./PlanNotesColumn";
import PlanOrderColumn from "./PlanOrderColumn";
import { PlanPickupColumn } from "./PlanPickupColumn";
import { PlanStatusFilter } from "./PlanStatusFilter";
import MemoizedToolbar from "./PlanToolbar";

const columnNames = [
    "index",
    "name",
    "companions",
    "estimatedPeopleCount",
    "pickUp",
    "location",
    "airport",
    "driverId",
    "notes",
    "transferType",
    "accommodation",
    "flightTime",
    "status",
    "patientType",
    "passengerType",
    "welcomeTeamVisibility",
];

export const defaultPlanGridOptions: GridInitialStatePremium = {
    sorting: {
        sortModel: [{ field: "pickUp", sort: "asc" }],
    },
    pinnedColumns: {
        left: ["index", "name"],
        right: ["actions"],
    },
    columns: {
        orderedFields: columnNames,
        columnVisibilityModel: columnNames.reduce(
            (acc, field) => {
                acc[field] = true;
                return acc;
            },
            {} as Record<string, boolean>
        ),
    },
};

function PlanTable({
    rows,
    loading,
    reloadTable,
    apiRef,
    allRows,
}: {
    rows: IPlanPassenger<"api">[];
    allRows: IPlanPassenger<"api">[];
    loading: boolean;
    reloadTable: () => void;
    apiRef: MutableRefObject<GridApiPremium>;
}) {
    const { t } = useTranslation();
    const { size } = useWindowDimensions();
    const rowModesModel = usePlanRowModesModel();

    const { processRowUpdate, handleRowModesModelCancel } = usePlanActions();
    const { drivers } = useDriversContext();
    const selectionModel = useRef<GridCellSelectionModel>();
    const setCellSelectionModel = useCallback(
        (selection: GridCellSelectionModel) => {
            selectionModel.current = selection;
        },
        [selectionModel]
    );

    const userPermissions = useAuth()?.userData?.permissionCodes || [];
    const canUpdate = checkPermissions(userPermissions, [PERMISSIONS.TR_SVC_MANAGE_DRIVERS]);

    const onCellKeyDown = useCallback<GridEventListener<"cellKeyDown">>(
        ({ cellMode, hasFocus, field, isEditable }, event, { api }) => {
            onGridCellKeyDownCallback(
                { cellMode, hasFocus, field, isEditable },
                event,
                { api },
                selectionModel.current || {},
                processRowUpdate,
                handleRowModesModelCancel
            );
        },
        [selectionModel, processRowUpdate, handleRowModesModelCancel]
    );

    const columns: GridColDef[] = useMemo<GridColDef[]>(
        () => [
            PlanOrderColumn(),
            PassengerColumn({ size, t, raw: false }),
            CompanionsColumn({ t, editable: true }),
            EstimatedPeopleCountColumn({
                t,
                getIsFlightArrival: (row) => row.isFlightArrival,
                getValueGetter: (row) => row.people,
                getCompanionsLength: (row) => row.companions.length,
            }),
            PlanPickupColumn({ t, editable: true }),
            PlanLocationColumn({
                t,
                editable: true,
                valueGetter: (row) => row.location,
                field: "location",
                headerName: "location",
            }),
            PlanAirportColumn({
                size,
                headerName: t("airport"),
                editable: true,
                valueGetter: (row) => row.airport,
            }),
            HotelNameColumn({ t }),
            PlanAssignationColumn({ t, drivers }),
            PlanNotesColumn({ t }),
            FlightTimeColumn({ t }),
            PassengerStatusColumn({ t, editable: true }),
            PatientTypeColumn({ t }),
            PassengerTransferTypeColumn({ t }),
            PassengerTypeColumn({ t }),
            PassengerVisibilityColumn({ t }),
            PlanActionsColumn(),
        ],
        [size, drivers, t]
    );

    const isFullScreen = usePlanIsFullScreen();

    return (
        <>
            <div className="col-span-12 flex flex-col w-full grow h-full">
                <motion.div
                    className="flex-1 min-h-0"
                    initial={{ position: "static", width: "100%" }}
                    animate={{
                        height: isFullScreen ? "100vh" : "auto",
                        width: isFullScreen ? "100vw" : "100%",
                        position: isFullScreen ? "fixed" : "static",
                        padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                        top: 0,
                        left: 0,
                        background: isFullScreen ? "white" : "transparent",
                        zIndex: isFullScreen ? 1000 : "auto",
                    }}
                    transition={{ duration: 0.3 }}
                >
                    <CommonGridTable
                        toolbar={() => <MemoizedToolbar apiRef={apiRef} reloadTable={reloadTable} rows={allRows} />}
                        rowSelectionModel={[]}
                        loading={loading}
                        rows={rows}
                        preventCellEdit={canUpdate}
                        columns={columns}
                        allRows={allRows}
                        rowModesModel={rowModesModel}
                        api={apiRef}
                        processRowUpdate={processRowUpdate}
                        startElement={PlanStatusFilter}
                        onCellKeyDown={onCellKeyDown}
                        onCellSelectionModelChange={setCellSelectionModel}
                    />
                </motion.div>
            </div>
        </>
    );
}

export default PlanTable;
